// TelcoTrack — dva koláče ziskovosti stavby: příjem (hotovost) a výdej (náklady) po položkách.

import type { EChartsOption } from 'echarts';
import { escapeHtmlForChartTooltip } from './manager-kpi-chart.echarts';
import { echartsEntranceMotion } from './stat-motion';
import type { FinanceProfitabilityChartRow } from './finance-profitability-chart.util';

const CATEGORY_COLOR: Record<string, { light: string; dark: string }> = {
  vykop: { light: '#2563eb', dark: '#60a5fa' },
  montaz: { light: '#059669', dark: '#34d399' },
  optika: { light: '#d97706', dark: '#fbbf24' },
  ostatni: { light: '#7c3aed', dark: '#c4b5fd' },
};

const RISK_BORDER = '#f87171';
const EMPTY_SLICE = { light: 'rgba(100,116,139,0.28)', dark: 'rgba(148,163,184,0.28)' };

function formatKc(value: number): string {
  return (
    new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) +
    ' Kč'
  );
}

/** Velká částka jako na grafech financí: mil. Kč, tis. Kč, jinak Kč. */
export function formatFinanceKcShort(value: number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    return `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 1 }).format(n / 1_000_000)} mil. Kč`;
  }
  if (abs >= 10_000) {
    return `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(n / 1_000)} tis. Kč`;
  }
  return formatKc(n);
}

function formatPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 1 }).format(value)} %`;
}

/** Podíl plánovaného PPD příjmu odpovídající aktuální hotovosti stavby. */
export function completionHotovostKc(plannedIncome: number, completionPercent: number): number {
  const planned = Math.max(0, Number(plannedIncome) || 0);
  const pct = Math.max(0, Number(completionPercent) || 0);
  return Math.round((planned * pct) / 100);
}

type PieSlice = {
  name: string;
  value: number;
  itemStyle: { color: string; borderColor?: string; borderWidth?: number };
  label?: { show: boolean };
  tooltip?: { show: boolean };
};

type CategorySlice = {
  id: string;
  label: string;
  color: string;
  incomeKc: number;
  expenseKc: number;
  over: boolean;
};

function categorySlices(rows: FinanceProfitabilityChartRow[], dark: boolean): CategorySlice[] {
  return rows
    .filter((row) => row.id !== 'celkem')
    .map((row) => {
      const palette = CATEGORY_COLOR[row.id] ?? CATEGORY_COLOR['ostatni'];
      const incomeKc = completionHotovostKc(row.plannedIncome, row.completionPercent);
      const expenseKc = Math.max(0, Number(row.actualCosts) || 0);
      return {
        id: row.id,
        label: row.label,
        color: dark ? palette.dark : palette.light,
        incomeKc,
        expenseKc,
        over: expenseKc > incomeKc + 0.5,
      };
    })
    .filter((slice) => slice.incomeKc > 0 || slice.expenseKc > 0);
}

function pieData(slices: CategorySlice[], side: 'income' | 'expense', emptyColor: string): PieSlice[] {
  const rows = slices
    .map((slice) => {
      const value = side === 'income' ? slice.incomeKc : slice.expenseKc;
      if (value <= 0) return null;
      const item: PieSlice = {
        name: slice.label,
        value,
        itemStyle: { color: slice.color },
      };
      if (side === 'expense' && slice.over) {
        item.itemStyle.borderColor = RISK_BORDER;
        item.itemStyle.borderWidth = 3;
      }
      return item;
    })
    .filter((item): item is PieSlice => item != null);
  if (rows.length > 0) return rows;
  return [
    {
      name: 'Bez částky',
      value: 1,
      itemStyle: { color: emptyColor },
      label: { show: false },
    },
  ];
}

function pieTitle(text: string, subtext: string, left: string, textColor: string, subColor: string) {
  return {
    text,
    subtext,
    left,
    top: 4,
    textAlign: 'center' as const,
    textStyle: { color: textColor, fontSize: 14, fontWeight: 600 },
    subtextStyle: { color: subColor, fontSize: 13, fontWeight: 500 },
  };
}

function chartLayout(width: number, height: number): {
  radius: number;
  incomeCenter: [string, string];
  expenseCenter: [string, string];
  incomeTitle: { left: string; top: number | string };
  expenseTitle: { left: string; top: number | string };
} {
  const narrow = width > 0 && width < 700;
  if (narrow) {
    return {
      radius: Math.max(64, Math.round(Math.min(width, height) * 0.16)),
      incomeCenter: ['50%', '30%'],
      expenseCenter: ['50%', '74%'],
      incomeTitle: { left: '50%', top: 0 },
      expenseTitle: { left: '50%', top: '48%' },
    };
  }
  return {
    radius: Math.max(72, Math.round(Math.min(width * 0.16, height * 0.28))),
    incomeCenter: ['26%', '56%'],
    expenseCenter: ['74%', '56%'],
    incomeTitle: { left: '26%', top: 0 },
    expenseTitle: { left: '74%', top: 0 },
  };
}

export function buildFinanceProfitabilityChartOption(args: {
  rows: FinanceProfitabilityChartRow[];
  dark: boolean;
  width?: number;
  height?: number;
  escapeHtml?: (s: string) => string;
  /** `true` skips the draw-in (prefers-reduced-motion). */
  reduceMotion?: boolean;
}): EChartsOption | null {
  const { rows, dark } = args;
  const escapeHtml = args.escapeHtml ?? escapeHtmlForChartTooltip;
  if (!rows.length) return null;

  const slices = categorySlices(rows, dark);
  const incomeTotal = slices.reduce((sum, slice) => sum + slice.incomeKc, 0);
  const expenseTotal = slices.reduce((sum, slice) => sum + slice.expenseKc, 0);
  const text = dark ? '#e5e5e5' : '#262626';
  const muted = dark ? '#a3a3a3' : '#525252';
  const empty = dark ? EMPTY_SLICE.dark : EMPTY_SLICE.light;
  const expenseTitleColor = expenseTotal > incomeTotal + 0.5 ? (dark ? '#fca5a5' : '#b91c1c') : text;
  const byLabel = new Map(slices.map((slice) => [slice.label, slice]));
  const layout = chartLayout(args.width ?? 960, args.height ?? 420);

  const sliceLabel = {
    position: 'inside' as const,
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 600 as const,
    textBorderColor: 'rgba(0,0,0,0.45)',
    textBorderWidth: 2,
    formatter: (params: unknown) => {
      const item = params as { name?: string; percent?: number };
      if (item.name === 'Bez částky') return '';
      const pct = Number(item.percent ?? 0);
      if (pct < 12) return '';
      return `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(pct)} %`;
    },
  };

  return {
    ...echartsEntranceMotion(args.reduceMotion),
    title: [
      { ...pieTitle('Příjem', formatFinanceKcShort(incomeTotal), layout.incomeTitle.left, text, muted), top: layout.incomeTitle.top },
      {
        ...pieTitle(
          'Výdej',
          formatFinanceKcShort(expenseTotal),
          layout.expenseTitle.left,
          expenseTitleColor,
          expenseTotal > incomeTotal + 0.5 ? expenseTitleColor : muted,
        ),
        top: layout.expenseTitle.top,
      },
    ],
    legend: {
      bottom: 0,
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { color: muted, fontSize: 12 },
      data: slices.map((slice) => ({
        name: slice.label,
        itemStyle: { color: slice.color },
      })),
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: dark ? 'rgba(23,23,23,0.95)' : 'rgba(255,255,255,0.98)',
      borderColor: dark ? '#404040' : '#d4d4d4',
      textStyle: { color: dark ? '#f5f5f5' : '#171717', fontSize: 12 },
      formatter: (params: unknown) => {
        const item = params as { name?: string };
        const slice = byLabel.get(String(item.name ?? ''));
        if (!slice) return 'Bez částky';
        const incomeShare = incomeTotal > 0 ? (slice.incomeKc / incomeTotal) * 100 : null;
        const expenseShare = expenseTotal > 0 ? (slice.expenseKc / expenseTotal) * 100 : null;
        const lines = [
          `<strong>${escapeHtml(slice.label)}</strong>`,
          `Příjem: ${formatKc(slice.incomeKc)} · ${formatPct(incomeShare)} koláče`,
          `Výdej: ${formatKc(slice.expenseKc)} · ${formatPct(expenseShare)} koláče`,
          `Rozdíl: ${formatKc(slice.incomeKc - slice.expenseKc)}`,
        ];
        if (slice.over) {
          lines.push('<span style="color:#f87171">Výdej je vyšší než příjem</span>');
        }
        return lines.join('<br/>');
      },
    },
    series: [
      {
        name: 'Příjem',
        type: 'pie',
        radius: layout.radius,
        center: layout.incomeCenter,
        data: pieData(slices, 'income', empty),
        label: sliceLabel,
        labelLine: { show: false },
      },
      {
        name: 'Výdej',
        type: 'pie',
        radius: layout.radius,
        center: layout.expenseCenter,
        data: pieData(slices, 'expense', empty),
        label: sliceLabel,
        labelLine: { show: false },
      },
    ],
  };
}
